import * as React from 'react'
import dynamic from 'next/dynamic'
import {motion} from 'framer-motion'
import get from 'lodash/get'

import Header from '@components/demo/Header'
import Footer from '@components/demo/Footer'
import Contact from '@components/demo/Contact'
import ImgSet from '@components/demo/ImgSet'
import UploadForm from '@components/demo/UploadForm'
import UploadResult from '@components/demo/UploadResult'
import CTAButton from '@components/demo/CTAButton'

import {t, axios, sleep} from '@lib/helpers'

const Meta = dynamic(() => import('@components/Meta'))

const Page = ({isRecaptchaValidated}) => {
  return (
    <React.Fragment>
      <Meta title={`WowDetection – ${t('layout.title')}`} />

      <div className='DemoDetail Detection'>
        <Header name='WowDetection' />

        <div className='DemoDetail__wrapper'>
          <Hero />
          <Why />
        </div>

        <Upload isRecaptchaValidated={isRecaptchaValidated} />

        <Contact isRecaptchaValidated={isRecaptchaValidated}>
          <div className='DemoDetail__spec'>
            <div className='container'>
              <p className='DemoDetail__spec__p'>
                {t('pages.demo.detection.spec')}
              </p>
            </div>
          </div>
        </Contact>

        <Footer />
      </div>
    </React.Fragment>
  )
}

const Hero = () => {
  const labels = [
    {
      type: t('pages.demo.detection.hero.label.building'),
      amount: 2,
    },
    {
      type: t('pages.demo.detection.hero.label.car'),
      amount: 1,
    },
    {
      type: t('pages.demo.detection.hero.label.bag'),
      amount: 3,
    },
    {
      type: t('pages.demo.detection.hero.label.clock'),
      amount: 1,
    },
    {
      type: t('pages.demo.detection.hero.label.people'),
      amount: 5,
    },
  ]
  const [currentIndex, setCurrentcurrentIndex] = React.useState(0)

  const nextLabel = () =>
    setCurrentcurrentIndex((index) => (index + 1) % labels.length)

  const {amount, type} = labels[currentIndex]

  return (
    <section className='DemoDetail__hero'>
      <div className='DemoDetail__feature'>
        <div className='DemoDetail__feature__img'>
          <ImgSet src='/images/demo/detection/feature.jpg' alt='City' />
        </div>

        {
          <div
            className={`DemoDetail__feature__labels DemoDetail__feature__labels--${type}`}
          >
            {Array.from({length: amount}).map((_, index) => (
              <motion.div
                key={`${type}${index}`}
                animate={{
                  scale: 0.98,
                }}
                transition={{
                  yoyo: Math.round(Math.random() * 4) + 2,
                  duration: 0.5,
                  ease: 'linear',
                }}
                onAnimationComplete={nextLabel}
              >
                <span>{type}</span>
              </motion.div>
            ))}
          </div>
        }
      </div>

      <div className='DemoDetail__hero__content'>
        <div className='container'>
          <h1 className='DemoDetail__title DemoDetail__title--hilight'>
            {t('pages.demo.detection.hero.title')}
          </h1>
          <h4>{t('pages.demo.detection.hero.subtitle')}</h4>
          <CTAButton />
        </div>
      </div>
    </section>
  )
}

const Why = () => {
  return (
    <section className='DemoDetail__why'>
      <div className='container' id='why'>
        <div className='DemoDetail__why__img'>
          <ImgSet
            src='/images/demo/detection/why.jpg'
            alt='WowDetection'
            loading='lazy'
          />
        </div>

        <div className='DemoDetail__why__content'>
          <h2
            className='DemoDetail__title DemoDetail__title--hilight'
            dangerouslySetInnerHTML={{
              __html: t('pages.demo.detection.why.title'),
            }}
          />
          <p>{t('pages.demo.detection.why.description')}</p>
        </div>
      </div>
    </section>
  )
}

const Upload = ({isRecaptchaValidated}) => {
  const [result, setResult] = React.useState('')

  const fetchResult = async (slug, retries) => {
    const response = await axios.get(`/demo/predictions/${slug}`, {
      headers: {
        'X-Api-Key': 'Data wow',
      },
    })

    if (get(response, 'data.data.status') === 'done') {
      const result = get(response, 'data.data.results.detector_result')
      const descending = (a, b) => b.probability - a.probability
      return result.sort(descending)
    }

    if (retries > 0) {
      await sleep(2000)
      return fetchResult(slug, retries - 1)
    }

    return 'Timeout! please try again'
  }

  const handleChange = async (file) => {
    const formData = new FormData()
    formData.append('image', file)
    setResult('')

    try {
      const postResponse = await axios.post('/demo/predictions', formData, {
        headers: {
          'X-Api-Key': 'Data wow',
        },
      })

      if (get(postResponse, 'data.status') !== 'OK') {
        setResult('Something wrong please try again.')
        return
      }

      const result = await fetchResult(get(postResponse, 'data.data.slug'), 15)
      setResult(JSON.stringify(result, null, 2))
    } catch (err) {
      setResult('Something wrong please try again.')
    }
  }

  return (
    <section className='DemoDetail__upload' id='upload'>
      <header>
        <div className='container'>
          <h2 className='DemoDetail__title DemoDetail__title--hilight'>
            WowDetection
          </h2>
        </div>
      </header>

      <hr />

      <div className='DemoDetail__upload__content'>
        <UploadForm
          name='WowDetection'
          onChange={handleChange}
          isLoading={isRecaptchaValidated}
        />

        <UploadResult result={result} />
      </div>
    </section>
  )
}

export default Page
